import type { TFrame } from '../models/playlist'
import type { TRssDirectoryEntry, TRssDirectoryRef, TRssDirectoryRefOverrides } from '../models/rssDirectory'
import { rssDirectoryRefOverridesSchema, rssDirectoryRefSchema } from '../models/rssDirectoryRef'
import { getRssDirectoryEntry, isRssDirectoryEntryId } from './rssDirectoryCatalog'
import { getRssDirectoryEntryFromCatalog, isRssDirectoryEntryInCatalog } from './rssDirectoryCatalogResolve'

const DEFAULT_RSS_SLIDE_COUNT = 3
const DEFAULT_RSS_SLIDE_DURATION_SEC = 10

export function resolveRssSlideCount(
  ref: TRssDirectoryRef,
  entry?: TRssDirectoryEntry | null
): number {
  return ref.overrides?.slideCount ?? entry?.defaults.slideCount ?? DEFAULT_RSS_SLIDE_COUNT
}

export function resolveRssSlideDurationSec(
  ref: TRssDirectoryRef,
  entry?: TRssDirectoryEntry | null
): number {
  return ref.overrides?.slideDurationSec ?? entry?.defaults.slideDurationSec ?? DEFAULT_RSS_SLIDE_DURATION_SEC
}

/** Total frame slot duration: item count × seconds per item. */
export function computeRssDirectoryFrameDuration(
  ref: TRssDirectoryRef,
  entry?: TRssDirectoryEntry | null
): number {
  const resolvedEntry = entry ?? getRssDirectoryEntry(ref.entryId)
  return resolveRssSlideCount(ref, resolvedEntry) * resolveRssSlideDurationSec(ref, resolvedEntry)
}

/** Raise understated playlist durations so RSS frames honor slideCount × slideDurationSec. */
export function ensureRssDirectoryFrameDuration<T extends Pick<TFrame, 'duration' | 'rssDirectoryRef'>>(
  frame: T
): T {
  if (!isRssDirectoryFrame(frame) || frame.rssDirectoryRef == null) {
    return frame
  }
  const expected = computeRssDirectoryFrameDuration(frame.rssDirectoryRef)
  if ((frame.duration ?? 0) >= expected) {
    return frame
  }
  return { ...frame, duration: expected }
}

export function rssDirectoryRefWithOverrides(
  ref: TRssDirectoryRef,
  patch: Partial<TRssDirectoryRefOverrides>
): TRssDirectoryRef {
  const entry = getRssDirectoryEntry(ref.entryId)
  const mergedOverrides = rssDirectoryRefOverridesSchema.parse({
    ...ref.overrides,
    ...patch
  })
  const durationSec = computeRssDirectoryFrameDuration(
    { ...ref, overrides: mergedOverrides },
    entry
  )
  return rssDirectoryRefSchema.parse({
    ...ref,
    overrides: { ...mergedOverrides, durationSec }
  })
}

export function rssDirectoryRefForEntry(
  entryId: string,
  carryOverrides?: TRssDirectoryRefOverrides,
  catalog?: readonly TRssDirectoryEntry[]
): TRssDirectoryRef | null {
  const entry = catalog
    ? getRssDirectoryEntryFromCatalog(entryId, catalog)
    : getRssDirectoryEntry(entryId)
  if (!entry) {
    return null
  }
  const overrides = carryOverrides
    ? rssDirectoryRefOverridesSchema.parse(carryOverrides)
    : undefined
  const ref = rssDirectoryRefSchema.parse({
    entryId: entry.id,
    layoutId: entry.layoutId,
    layoutOptions: entry.layoutOptions,
    ...(overrides ? { overrides } : {})
  })
  if (!overrides) {
    return ref
  }
  return rssDirectoryRefWithOverrides(ref, overrides)
}

export function applyRssDirectoryFrameUpdate(frame: TFrame, nextRef: TRssDirectoryRef): TFrame {
  const entry = getRssDirectoryEntry(nextRef.entryId)
  return {
    ...frame,
    layout: entry?.layout ?? frame.layout,
    duration: computeRssDirectoryFrameDuration(nextRef, entry),
    rssDirectoryRef: nextRef
  }
}

export function isRssDirectoryFrame(frame: Pick<TFrame, 'rssDirectoryRef'>): boolean {
  return frame.rssDirectoryRef != null
}

export function createRssDirectoryFrameFromEntry(
  entry: TRssDirectoryEntry,
  frameId: string,
  overrides?: TRssDirectoryRefOverrides
): TFrame {
  const rssDirectoryRef = rssDirectoryRefSchema.parse({
    entryId: entry.id,
    layoutId: entry.layoutId,
    layoutOptions: entry.layoutOptions,
    ...(overrides && Object.keys(overrides).length > 0 ? { overrides } : {})
  })
  const duration = computeRssDirectoryFrameDuration(rssDirectoryRef, entry)
  return {
    id: frameId,
    layout: entry.layout,
    duration,
    facets: {},
    rssDirectoryRef
  }
}

export function validateRssDirectoryFrame(
  frame: TFrame,
  options?: { enabledCatalog?: readonly TRssDirectoryEntry[] }
): { valid: true } | { valid: false, message: string } {
  if (!frame.rssDirectoryRef) {
    return { valid: false, message: 'missing rssDirectoryRef' }
  }
  const entryId = frame.rssDirectoryRef.entryId
  const catalog = options?.enabledCatalog
  const known = catalog
    ? isRssDirectoryEntryInCatalog(entryId, catalog)
    : isRssDirectoryEntryId(entryId)
  if (!known) {
    return { valid: false, message: `unknown rss directory entry: ${entryId}` }
  }
  if (Object.keys(frame.facets ?? {}).length > 0) {
    return { valid: false, message: 'rss directory frames must not have facets' }
  }
  return { valid: true }
}
